import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Eye, ShieldCheck, Clock, KeyRound } from "lucide-react";
import { scrollToId, imgSrc } from "@/lib/api";

const MaskedLine = ({ text, cls, delay, start }) => (
  <span className="block overflow-hidden pb-2 -mb-2">
    <motion.span
      className={`block ${cls}`}
      initial={{ y: "115%" }}
      animate={{ y: start ? "0%" : "115%" }}
      transition={{ duration: 0.95, delay: start ? delay : 0, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export default function Hero({ site, start = true }) {
  const LINES = [
    { text: site.hero_line1, cls: "text-white" },
    { text: site.hero_line2, cls: "text-white" },
    { text: site.hero_line3, cls: "text-[#00F0FF] text-glow" },
  ];
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 55, damping: 14 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 55, damping: 14 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="hero-radial relative overflow-hidden pt-36 pb-24 lg:pt-44 lg:pb-32"
      data-testid="hero-section"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-slate-300"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#00F0FF]" />
            Tarbyte · Ideas Engineered Digitally
          </motion.div>

          <h1 className="font-display text-5xl font-black leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {LINES.map((l, i) => (
              <MaskedLine key={l.text} text={l.text} cls={l.cls} delay={0.55 + i * 0.13} start={start} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-subtext"
          >
            {site.hero_subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 1.25 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-consultation-btn"
              onClick={() => scrollToId("contact")}
              className="group flex items-center gap-2.5 rounded-full bg-[#00F0FF] px-7 py-3.5 text-sm font-bold text-[#050811] transition-shadow duration-300 hover:shadow-[0_0_36px_rgba(0,240,255,0.6)]"
            >
              Get a Free Consultation
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
            <button
              data-testid="hero-view-work-btn"
              onClick={() => scrollToId("work")}
              className="group flex items-center gap-2.5 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors duration-300 hover:border-[#00F0FF]/60 hover:text-[#00F0FF]"
            >
              <Eye size={17} />
              View Our Work
            </button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={start ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 1.45 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-slate-400"
          >
            {[
              [ShieldCheck, "Transparent pricing"],
              [KeyRound, "You own your website"],
              [Clock, "Clear timelines"],
            ].map(([Icon, label]) => (
              <li key={label} className="flex items-center gap-2">
                <Icon size={15} className="text-[#00F0FF]" />
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={start ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
          style={{ perspective: 1200 }}
        >
          <motion.div
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative max-h-[80vh] overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,240,255,0.25)]"
            data-testid="hero-visual"
          >
            <img
              src={imgSrc(site.hero_image)}
              alt="Abstract iridescent blue sculpture representing digital craft"
              className="h-[520px] w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050811]/80 via-transparent to-transparent" />
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute left-5 top-5 rounded-2xl border border-white/15 bg-black/50 px-4 py-3 backdrop-blur-xl"
              style={{ transform: "translateZ(50px)" }}
            >
              <p className="font-display text-2xl font-extrabold text-[#00F0FF]">100%</p>
              <p className="text-xs text-slate-300">Ownership of your site</p>
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute bottom-5 right-5 rounded-2xl border border-white/15 bg-black/50 px-4 py-3 backdrop-blur-xl"
              style={{ transform: "translateZ(40px)" }}
            >
              <p className="font-display text-2xl font-extrabold text-white">10+</p>
              <p className="text-xs text-slate-300">Services under one roof</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
